import React, { useState, useEffect, useRef } from 'react';
import Recaptcha from 'react-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { getUrlParameter } from '@rsos/utils';
import { getRecaptchaSiteKey } from '@rsos/utils/metaTags';
import { dismissAllToasts, notifyError } from '@rsos/utils/toastUtils';
import { confirmUser, getOAuthToken, resetFailedCount } from '../../../actions';
import styles from './NewLogin.module.css';

const PORTAL_EULA_LINK = 'https://rapidsos.com/ecc-end-user-license/';
const PRIVACY_POLICY_LINK =
  'https://rapidsos.com/legal/emergency-related-services-privacy-policy/';

const validate = values => {
  const errors = {};
  if (!values.email) {
    errors.email = 'Email is required';
  }
  if (!values.password) {
    errors.password = 'Password is required';
  }
  return errors;
};

const NewLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(
    state => state.sinatra.auth.loading || state.sinatra.user.loading,
  );
  const isGettingPSAPInfo = useSelector(state => state.psaps.isGettingPSAPInfo);
  const failedCount = useSelector(state => state.auth.failedCount);
  const failedCountExpiration = useSelector(
    state => state.auth.failedCountExpiration,
  );

  const isLoggingIn = isLoading || isGettingPSAPInfo;
  const [verifiedRecaptcha, setVerifiedRecaptcha] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const recaptchaInstance = useRef(null);
  const failedCountResetTimer = useRef(null);

  useEffect(() => {
    const token = getUrlParameter('token');
    const isSessionExpired = getUrlParameter('session_expired') === 'true';

    if (isSessionExpired) {
      // Show once after redirect from apps when refresh-token renewal failed or
      // the wall-clock portal session expired. Stale API-error toasts from the
      // app being unloaded are misleading on the login page, so clear them
      // before displaying the intentional session-expired message.
      dismissAllToasts();
      notifyError('Your session has expired. Please sign in again.');
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (token) {
      confirmUser(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (failedCountResetTimer.current) {
      clearTimeout(failedCountResetTimer.current);
      failedCountResetTimer.current = null;
    }

    if (failedCount <= 0 || !failedCountExpiration) {
      return undefined;
    }

    const millisecondsUntilReset = failedCountExpiration - Date.now();

    if (millisecondsUntilReset <= 0) {
      dispatch(resetFailedCount());
      return undefined;
    }

    // The client stores failedCountExpiration for 15 minutes so reloads keep the
    // same reCAPTCHA window. This timer is only UX state reset; server-side rate
    // limiting remains the actual abuse control.
    failedCountResetTimer.current = setTimeout(() => {
      dispatch(resetFailedCount());
      failedCountResetTimer.current = null;
    }, millisecondsUntilReset);

    return () => {
      if (failedCountResetTimer.current) {
        clearTimeout(failedCountResetTimer.current);
        failedCountResetTimer.current = null;
      }
    };
  }, [dispatch, failedCount, failedCountExpiration]);

  const expiredCallback = () => {
    notifyError(
      ' Recaptcha verification has expired. Recheck the checkbox to\n' +
        ' continue.',
    );
    setVerifiedRecaptcha(false);
  };

  const onloadCallback = () => {
    // It's required to have render="explicit" and onloadCallback otherwise
    // Uncaught Error: ReCAPTCHA placeholder element must be empty
    // will be thrown
  };

  const verifyCallback = () => {
    setRecaptchaError(false);
    setVerifiedRecaptcha(true);
  };

  const handleSubmit = values => {
    const email = values.email.split(' ').join('').trim();
    if (failedCount < 5 || verifiedRecaptcha) {
      dispatch(getOAuthToken(email, values.password, navigate));
    } else {
      if (recaptchaError) {
        notifyError('Please check the recaptcha checkbox.');
      } else if (verifiedRecaptcha) {
        dispatch(getOAuthToken(email, values.password, null));
      } else {
        setRecaptchaError(true);
        recaptchaInstance.current?.reset?.();
      }
    }
  };

  return (
    <div className={styles.loginCard}>
      <Formik
        initialValues={{ email: '', password: '' }}
        validate={validate}
        validateOnBlur
        validateOnChange={false}
        onSubmit={values => handleSubmit(values)}
      >
        {({ values, errors, touched, handleBlur, handleChange }) => (
          <Form className={styles.form} name="login-form">
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="email">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="text"
                className={`${styles.input}${
                  touched.email && errors.email ? ` ${styles.inputError}` : ''
                }`}
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={50}
              />
              <div
                className={`${styles.fieldError}${
                  touched.email && errors.email
                    ? ` ${styles.fieldErrorVisible}`
                    : ''
                }`}
              >
                {touched.email && errors.email ? errors.email : '\u00A0'}
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="password">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                className={`${styles.input}${
                  touched.password && errors.password
                    ? ` ${styles.inputError}`
                    : ''
                }`}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={256}
              />
              <div
                className={`${styles.fieldError}${
                  touched.password && errors.password
                    ? ` ${styles.fieldErrorVisible}`
                    : ''
                }`}
              >
                {touched.password && errors.password
                  ? errors.password
                  : '\u00A0'}
              </div>
            </div>

            {failedCount >= 5 && (
              <Recaptcha
                expiredCallback={expiredCallback}
                ref={ref => {
                  recaptchaInstance.current = ref;
                }}
                render="explicit"
                sitekey={getRecaptchaSiteKey()}
                type="image"
                onloadCallback={onloadCallback}
                verifyCallback={verifyCallback}
              />
            )}

            <div className={styles.buttonGroup}>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={
                  isLoggingIn || (failedCount >= 5 && !verifiedRecaptcha)
                }
              >
                {isLoggingIn ? <span className={styles.spinner} /> : 'Log in'}
              </button>
              <Link
                id="loginToSignUp"
                to="/sign-up"
                className={styles.secondaryButton}
              >
                New agency sign up
              </Link>
            </div>

            <div className={styles.forgotPasswordRow}>
              <Link
                id="forgotPassword"
                to="/request-password"
                className={styles.forgotPasswordLink}
              >
                Forgot Password?
              </Link>
            </div>

            <div className={styles.horizontalLine} />

            <Link id="loginWithSSO" to="/sso" className={styles.ssoButton}>
              Log in with SSO
            </Link>

            <div className={styles.horizontalLine} />

            <p className={styles.legalText}>
              By Signing up or logging in, you agree to be bound by the{' '}
              <a
                className={styles.legalLink}
                href={PORTAL_EULA_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                Unite EULA
              </a>{' '}
              and{' '}
              <a
                className={styles.legalLink}
                href={PRIVACY_POLICY_LINK}
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>
              .
            </p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default NewLogin;
